import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { QRCodeSVG } from 'qrcode.react';
import { FiEdit, FiTrash2, FiUserPlus, FiUsers } from 'react-icons/fi';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';
import { Modal, Button, Spin, Empty } from 'antd';
import { DownloadOutlined, PrinterOutlined } from '@ant-design/icons';
import { Typography } from 'antd';

// QRCode 컴포넌트를 조건부로 임포트
let QRCodeComponent;
try {
  const QRCodeModule = require('qrcode.react');
  QRCodeComponent = QRCodeModule.QRCodeSVG || QRCodeModule.default;
} catch (error) {
  console.error('QR 코드 모듈을 불러올 수 없습니다:', error);
  // QRCodeComponent는 undefined로 남음
}

// QR 코드 렌더링 컴포넌트
const QRCodeRenderer = ({ value }) => {
  if (!value) {
    return (
      <div className="w-[250px] h-[250px] flex items-center justify-center bg-gray-100">
        <p className="text-gray-500">QR 코드 데이터가 없습니다.</p>
      </div>
    );
  }
  
  if (!QRCodeComponent) {
    return (
      <div className="w-[250px] h-[250px] flex items-center justify-center bg-gray-100">
        <p className="text-gray-500">QR 코드 생성 모듈을 불러올 수 없습니다.</p>
      </div>
    );
  }
  
  return (
    <QRCodeSVG 
      value={value}
      size={250}
      level="H"
    />
  );
};

const ClassManagement = () => {
  const { authAxios, user } = useAuth();
  
  // 사용자 정보 디버깅
  useEffect(() => {
    console.log('현재 인증된 사용자 정보:', user);
    console.log('사용자 ID 정보 - id:', user?._id);
  }, [user]);
  
  const [classes, setClasses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAddingClass, setIsAddingClass] = useState(false);
  const [isEditingClass, setIsEditingClass] = useState(false);
  const [currentClass, setCurrentClass] = useState(null);
  const [instructors, setInstructors] = useState([]);
  const [isViewingStudents, setIsViewingStudents] = useState(false);
  const [currentStudents, setCurrentStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [currentQRCode, setCurrentQRCode] = useState(null);
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrLoading, setQrLoading] = useState(false); // qrLoading 상태 추가
  
  // 폼 제출 중인지 여부를 추적하는 상태 추가
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // 전체 교육생 목록 관련 상태 추가
  const [allStudents, setAllStudents] = useState([]);
  const [studentsToAdd, setStudentsToAdd] = useState([]);
  const [loadingAllStudents, setLoadingAllStudents] = useState(false);
  
  // 인라인 편집 관련 상태 추가
  const [editingRow, setEditingRow] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    instructorId: '',
  });
  
  // 기수별 탭 관련 상태 추가
  const [activeStudentTab, setActiveStudentTab] = useState('all');
  const [studentBatches, setStudentBatches] = useState([]);
  
  // 출결관리 관련 상태 추가
  const [isAttendanceMode, setIsAttendanceMode] = useState(false);
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [attendanceList, setAttendanceList] = useState([]);
  const [isAddingAttendance, setIsAddingAttendance] = useState(false);
  const [isEditingAttendance, setIsEditingAttendance] = useState(false);
  const [currentAttendance, setCurrentAttendance] = useState(null);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  
  // 출결현황 관련 상태 추가
  const [showAttendanceStatus, setShowAttendanceStatus] = useState(false);
  const [attendanceStatusList, setAttendanceStatusList] = useState([]);
  const [attendanceStatusLoading, setAttendanceStatusLoading] = useState(false);
  const [attendanceStats, setAttendanceStats] = useState({
    totalStudents: 0,
    presentCount: 0,
    lateCount: 0,
    absentCount: 0
  });
  
  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm();

  // 1. activeTab 초기값 변경 (student로 설정)
  const [activeTab, setActiveTab] = useState('student'); // 'instructor', 'student', 'admin'

  // 정렬 및 필터링 관련 상태 추가
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [filters, setFilters] = useState({
    instructorId: '',
    category: '',
    structure: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  // 강의 목록 가져오기
  const fetchClasses = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // API 호출
      const response = await authAxios.get('/classes');
      const classesData = response.data;
      
      // MongoDB _id를 id로 매핑
      const mappedClasses = classesData.map(classItem => ({
        ...classItem,
        id: classItem._id || classItem.id, // MongoDB의 _id를 id로 사용
        students: classItem.studentIds ? classItem.studentIds.length : 0 // 학생 수 계산
      }));
      
      // 디버깅: 강의 데이터와 ID 확인
      console.log('원본 강의 데이터:', classesData);
      console.log('변환된 강의 데이터:', mappedClasses);
      mappedClasses.forEach((c, index) => {
        console.log(`강의 ${index + 1} ID:`, c.id, typeof c.id);
        console.log(`강의 ${index + 1} 학생 수:`, c.students);
      });
      
      setClasses(mappedClasses);
      setIsLoading(false);
    } catch (err) {
      setError('강의 목록을 불러오는 중 오류가 발생했습니다.');
      setIsLoading(false);
      console.error('Classes list error:', err);
    }
  }, [authAxios]);

  // 강사 목록 가져오기
  const fetchInstructors = useCallback(async () => {
    try {
      // API 호출 - 강사 역할만 필터링
      const response = await authAxios.get('/users');
      
      // _id를 id로 통일하고 강사 역할만 필터링
      const mappedInstructors = response.data
        .filter(user => Array.isArray(user.roles) && user.roles.includes('instructor')) // 강사 역할만 필터링
        .map(instructor => ({
          ...instructor,
          id: instructor._id || instructor.id,
          role: 'instructor' // 강사 역할 명시적 설정
        }));
      
      console.log('강사 목록 로드:', mappedInstructors);
      setInstructors(mappedInstructors);
    } catch (err) {
      console.error('Instructors fetch error:', err);
      // 임시 데이터 (API 연동 실패 시 사용)
      setInstructors([
        { id: '1', name: '김강사', role: 'instructor' },
        { id: '2', name: '박강사', role: 'instructor' },
        { id: '3', name: '이강사', role: 'instructor' },
        { id: '4', name: '최강사', role: 'instructor' },
      ]);
    }
  }, [authAxios]);

  // 모든 교육생 목록 가져오기
  const fetchAllStudents = useCallback(async () => {
    try {
      setLoadingAllStudents(true);
      const response = await authAxios.get('/students');
      
      // MongoDB _id를 id로 매핑
      const mappedStudents = response.data.map(student => ({
        ...student,
        id: student._id || student.id
      }));
      
      setAllStudents(mappedStudents);
      
      // 기수 정보 추출 및 중복 제거
      const batches = [...new Set(mappedStudents.map(student => student.batch || '미지정'))].sort();
      setStudentBatches(batches);
      
      setLoadingAllStudents(false);
    } catch (err) {
      console.error('All students fetch error:', err);
      setLoadingAllStudents(false);
      // 임시 데이터 (API 연동 실패 시 사용)
      const mockStudents = [
        { id: '1', name: '홍길동', studentId: '20230001', batch: '1기' },
        { id: '2', name: '김철수', studentId: '20230002', batch: '1기' },
        { id: '3', name: '이영희', studentId: '20230003', batch: '2기' },
        { id: '4', name: '박지민', studentId: '20230004', batch: '2기' },
        { id: '5', name: '최유리', studentId: '20230005', batch: '3기' },
      ];
      
      setAllStudents(mockStudents);
      
      // 임시 기수 정보
      const batches = [...new Set(mockStudents.map(student => student.batch || '미지정'))].sort();
      setStudentBatches(batches);
    }
  }, [authAxios]);

  // 강의 교육생 목록 가져오기
  const fetchClassStudents = async (classId) => {
    try {
      setStudentsLoading(true);
      console.log(`강의(ID: ${classId})의 교육생 목록 요청 시작`);
      
      const response = await authAxios.get(`/classes/${classId}/students`);
      
      // MongoDB _id를 id로 매핑
      const mappedStudents = response.data.map(student => ({
        ...student,
        id: student._id || student.id // MongoDB의 _id를 id로 사용
      }));
      
      console.log('교육생 목록 조회 성공:', mappedStudents);
      
      setCurrentStudents(mappedStudents);
      setStudentsLoading(false);
    } catch (err) {
      console.error('강의 교육생 목록 조회 오류:', err);
      
      if (err.response) {
        console.error('응답 상태:', err.response.status);
        console.error('응답 데이터:', err.response.data);
        toast.error(`교육생 목록을 불러올 수 없습니다: ${err.response.status} 오류`);
      } else if (err.request) {
        console.error('서버 응답 없음:', err.request);
        toast.error('서버에 연결할 수 없습니다. 네트워크 연결을 확인하세요.');
      } else {
        toast.error(`교육생 목록 로딩 중 오류: ${err.message}`);
      }
      
      setStudentsLoading(false);
      
      // 임시 데이터 (API 연동 실패 시 사용)
      console.warn('백업 데이터를 사용합니다.');
      setCurrentStudents([
        { id: '1', name: '홍길동', studentId: '20230001' },
        { id: '2', name: '김철수', studentId: '20230002' },
        { id: '3', name: '이영희', studentId: '20230003' },
      ]);
    }
  };

  // 출석 목록 가져오기
  const fetchAttendanceList = async (classId, date) => {
    try {
      setAttendanceLoading(true);
      
      // API 호출
      const response = await authAxios.get('/attendance', {
        params: { classId, date }
      });
      
      // 응답 데이터 매핑
      const mappedAttendance = response.data.map(attendance => ({
        ...attendance,
        id: attendance._id || attendance.id
      }));
      
      setAttendanceList(mappedAttendance);
      setAttendanceLoading(false);
    } catch (err) {
      console.error('Attendance list error:', err);
      setAttendanceLoading(false);
      
      // 임시 데이터 (API 연동 실패 시 사용)
      setAttendanceList([
        { id: '1', studentId: '1', studentName: '홍길동', classId, className: '자바 프로그래밍', date, status: 'present', time: '09:05:23' },
        { id: '2', studentId: '2', studentName: '김철수', classId, className: '자바 프로그래밍', date, status: 'late', time: '09:15:47' },
        { id: '3', studentId: '3', studentName: '이영희', classId, className: '자바 프로그래밍', date, status: 'present', time: '09:02:12' },
      ]);
    }
  };

  // 출결현황 데이터 가져오기
  const fetchAttendanceStatus = async (classId) => {
    try {
      setAttendanceStatusLoading(true);
      const today = new Date().toISOString().split('T')[0];
      
      // 1. 강의에 등록된 학생 목록 가져오기
      const studentsResponse = await authAxios.get(`/classes/${classId}/students`);
      const students = studentsResponse.data.map(student => ({
        ...student,
        id: student._id || student.id,
      }));
      
      // 2. 오늘 출석 데이터 가져오기
      const attendanceResponse = await authAxios.get('/attendance', {
        params: { classId, date: today }
      });
      
      const attendanceData = attendanceResponse.data;
      
      // 3. 학생별 출결 상태 매핑
      const statusList = students.map(student => {
        // 해당 학생의 출석 기록 찾기
        const attendance = attendanceData.find(a => 
          (a.studentId === student.id || a.studentId === student._id)
        );
        
        return {
          id: student.id,
          name: student.name,
          phone: student.studentId, // 학생 ID를 전화번호로 사용 (실제로는 전화번호 필드 사용)
          status: attendance ? attendance.status : 'absent', // 출석 기록이 없으면 결석으로 처리
          time: attendance ? attendance.time : '-',
          attendanceId: attendance ? attendance._id || attendance.id : null
        };
      });
      
      // 4. 출결 통계 계산
      const stats = {
        total: statusList.length,
        present: statusList.filter(s => s.status === 'present' || s.status === 'late').length,
        absent: statusList.filter(s => s.status === 'absent').length
      };
      
      // 5. 상태 업데이트
      setAttendanceStatusList(statusList);
      setAttendanceStats(stats);
      setAttendanceStatusLoading(false);
      
      return { statusList, stats };
    } catch (err) {
      console.error('출결현황 데이터 가져오기 오류:', err);
      setAttendanceStatusLoading(false);
      
      // 임시 데이터 (API 연동 실패 시 사용)
      const mockStatusList = [
        { id: '1', name: '홍길동', phone: '010-1234-5678', status: 'present', time: '09:05:23', attendanceId: '1' },
        { id: '2', name: '김철수', phone: '010-2345-6789', status: 'late', time: '09:15:47', attendanceId: '2' },
        { id: '3', name: '이영희', phone: '010-3456-7890', status: 'absent', time: '-', attendanceId: null },
        { id: '4', name: '박지민', phone: '010-4567-8901', status: 'present', time: '09:02:12', attendanceId: '3' },
        { id: '5', name: '최유리', phone: '010-5678-9012', status: 'absent', time: '-', attendanceId: null },
      ];
      
      const mockStats = {
        total: mockStatusList.length,
        present: mockStatusList.filter(s => s.status === 'present' || s.status === 'late').length,
        absent: mockStatusList.filter(s => s.status === 'absent').length
      };
      
      setAttendanceStatusList(mockStatusList);
      setAttendanceStats(mockStats);
      
      return { statusList: mockStatusList, stats: mockStats };
    }
  };
  
  // 출결 상태 변경
  const toggleAttendanceStatus = async (student) => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const currentTime = new Date().toLocaleTimeString();
      
      // 현재 상태 가져오기
      const currentStatus = student.status;
      
      // 다음 상태 결정 (present -> late -> absent -> present)
      let nextStatus;
      if (currentStatus === 'present') nextStatus = 'late';
      else if (currentStatus === 'late') nextStatus = 'absent';
      else nextStatus = 'present';
      
      let updatedStudent;
      
      // 1. 이미 출석 기록이 있는 경우 (API 업데이트)
      if (student.attendanceId) {
        const response = await authAxios.put(`/attendance/${student.attendanceId}`, {
          status: nextStatus,
          time: nextStatus !== 'absent' ? currentTime : null
        });
        
        updatedStudent = {
          ...student,
          status: nextStatus,
          time: nextStatus !== 'absent' ? currentTime : '-'
        };
      } 
      // 2. 출석 기록이 없는 경우 (새 기록 생성)
      else if (nextStatus !== 'absent') {
        const response = await authAxios.post('/attendance', {
          classId: currentClass.id,
          studentId: student.id,
          date: today,
          status: nextStatus,
          time: currentTime,
          registeredBy: user?.id || user?._id
        });
        
        updatedStudent = {
          ...student,
          status: nextStatus,
          time: currentTime,
          attendanceId: response.data._id || response.data.id
        };
      }
      // 3. 출석→미출석으로 변경 (출석 기록 삭제 필요 없음)
      else {
        updatedStudent = {
          ...student,
          status: 'absent',
          time: '-',
          attendanceId: null
        };
      }
      
      // 출결 목록 업데이트
      setAttendanceStatusList(prev => 
        prev.map(s => s.id === student.id ? updatedStudent : s)
      );
      
      // 출결 통계 업데이트
      const updatedList = [...attendanceStatusList.filter(s => s.id !== student.id), updatedStudent];
      const updatedStats = {
        total: updatedList.length,
        present: updatedList.filter(s => s.status === 'present' || s.status === 'late').length,
        absent: updatedList.filter(s => s.status === 'absent').length
      };
      
      setAttendanceStats(updatedStats);
      
      // 상태 변경 성공 메시지
      toast.success(`${student.name}님의 출결 상태가 ${getStatusDisplayName(nextStatus)}(으)로 변경되었습니다.`);
      
      return updatedStudent;
    } catch (err) {
      console.error('출결 상태 변경 오류:', err);
      toast.error('출결 상태 변경 중 오류가 발생했습니다.');
      return student;
    }
  };
  
  // 출결 상태별 표시 이름
  const getStatusDisplayName = (status) => {
    switch (status) {
      case 'present': return '출석';
      case 'late': return '지각';
      case 'absent': return '결석';
      case 'excused': return '사유결석';
      default: return '미정';
    }
  };
  
  // 출결 상태별 색상
  const getStatusColor = (status) => {
    switch (status) {
      case 'present':
        return 'bg-green-100 text-green-800';
      case 'late':
        return 'bg-yellow-100 text-yellow-800';
      case 'absent':
        return 'bg-red-100 text-red-800';
      case 'excused':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  // 출결현황 데이터를 엑셀로 내보내기
  const exportAttendanceToExcel = () => {
    try {
      // 현재 날짜 가져오기
      const today = new Date();
      const dateStr = today.toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).replace(/\. /g, '-').replace(/\./g, '');
      
      // 파일명 생성
      const fileName = `출결현황_${currentClass?.name || '강의'}_${dateStr}.csv`;
      
      // 헤더 행
      let csvContent = "번호,이름,연락처,출결상태,시간\n";
      
      // 데이터 행 추가
      attendanceStatusList.forEach((student, index) => {
        const statusText = getStatusDisplayName(student.status);
        const row = [
          index + 1,
          student.name,
          student.phone,
          statusText,
          student.time
        ].join(',');
        csvContent += row + '\n';
      });
      
      // BOM 추가 (한글 깨짐 방지)
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
      
      // 다운로드 링크 생성 및 클릭
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success(`출결 현황이 성공적으로 내보내졌습니다: ${fileName}`);
    } catch (err) {
      console.error('출결 현황 내보내기 오류:', err);
      toast.error('출결 현황을 내보내는 중 오류가 발생했습니다.');
    }
  };
  
  // QR 코드 다운로드 함수
  const downloadQRCode = () => {
    try {
      const svgElement = document.getElementById('qrcode-svg');
      if (!svgElement) {
        toast.error('QR 코드를 찾을 수 없습니다');
        return;
      }

      // SVG를 Canvas로 변환
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const svgData = new XMLSerializer().serializeToString(svgElement);
      const img = new Image();
      
      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        
        // Canvas를 이미지로 변환하여 다운로드
        const a = document.createElement('a');
        a.download = `출석QR코드_${currentClass?.name || '강의'}_${new Date().toISOString().slice(0, 10)}.png`;
        a.href = canvas.toDataURL('image/png');
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      };

      img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
    } catch (error) {
      console.error('QR 코드 다운로드 중 오류:', error);
      toast.error('QR 코드 다운로드 중 오류가 발생했습니다');
    }
  };
  
  // QR 코드 프린트 함수
  const printQRCode = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>출석 QR 코드 인쇄</title>
          <style>
            body { 
              font-family: Arial, sans-serif;
              text-align: center;
              padding: 20px;
            }
            .qr-container {
              margin: 0 auto;
              max-width: 400px;
            }
            .qr-url {
              word-break: break-all;
              margin: 15px 0;
            }
            svg {
              display: block;
              margin: 0 auto;
            }
          </style>
        </head>
        <body>
          <div class="qr-container">
            <h2>출석 QR 코드</h2>
            <p>강의: ${currentClass?.name}</p>
            <p>유효시간: 15분</p>
            <div>${document.getElementById('qrcode-svg')?.outerHTML || ''}</div>
            <p class="qr-url">URL: ${currentQRCode}</p>
          </div>
          <script>
            window.onload = function() { window.print(); }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };
  
  // 출결현황 보기
  const viewAttendanceStatus = async (classItem) => {
    try {
      setCurrentClass(classItem);
      setShowAttendanceStatus(true);
      setAttendanceStatusLoading(true);
      
      const response = await authAxios.get(`/api/attendance/status/${classItem.id}?date=${selectedDate}`);
      setAttendanceStatusList(response.data.attendanceList);
      
      // 통계 계산
      const total = response.data.attendanceList.length;
      const present = response.data.attendanceList.filter(s => s.status === 'present').length;
      const late = response.data.attendanceList.filter(s => s.status === 'late').length;
      const absent = response.data.attendanceList.filter(s => s.status === 'absent').length;
      
      setAttendanceStats({
        totalStudents: total,
        presentCount: present,
        lateCount: late,
        absentCount: absent
      });
    } catch (error) {
      console.error('출결현황 조회 실패:', error);
      toast.error('출결현황을 불러오는데 실패했습니다.');
    } finally {
      setAttendanceStatusLoading(false);
    }
  };
  
  // 날짜 변경 시 출결현황 다시 조회
  useEffect(() => {
    if (showAttendanceStatus && currentClass) {
      viewAttendanceStatus(currentClass);
    }
  }, [selectedDate]);

  // 학생 등록/제거 토글
  const toggleStudentEnrollment = async (classId, studentId, isEnrolled) => {
    try {
      setStudentsLoading(true);
      
      if (isEnrolled) {
        // 이미 등록된 학생은 제거
        const response = await authAxios.delete(`/classes/${classId}/students/${studentId}`);
        
        // API 호출이 성공한 경우에만 UI 업데이트
        if (response.status === 200 || response.status === 204) {
          // 현재 학생 목록에서 제거
          setCurrentStudents(prevStudents => 
            prevStudents.filter(student => student.id !== studentId && student._id !== studentId)
          );

          // 강의 목록의 학생 수 업데이트
          setClasses(prevClasses => 
            prevClasses.map(classItem => {
              if (classItem.id === classId) {
                return {
                  ...classItem,
                  students: Math.max(0, (classItem.students || 0) - 1) // 0보다 작아지지 않도록
                };
              }
              return classItem;
            })
          );
          
          toast.success('교육생이 성공적으로 제외되었습니다.');
        }
      } else {
        // 새 학생 등록
        const response = await authAxios.post(`/classes/${classId}/students`, { studentId });
        
        // API 호출이 성공한 경우에만 UI 업데이트
        if (response.status === 200 || response.status === 201) {
          // 새로 등록된 학생 정보를 응답에서 가져와서 목록에 추가
          const newStudent = response.data;
          setCurrentStudents(prevStudents => [...prevStudents, {
            ...newStudent,
            id: newStudent._id || newStudent.id // MongoDB의 _id를 id로 사용
          }]);
          
          // 강의 목록의 학생 수 업데이트
          setClasses(prevClasses => 
            prevClasses.map(classItem => {
              if (classItem.id === classId) {
                return {
                  ...classItem,
                  students: (classItem.students || 0) + 1
                };
              }
              return classItem;
            })
          );
          
          toast.success('교육생이 성공적으로 등록되었습니다.');
        }
      }
    } catch (err) {
      console.error('Student enrollment toggle error:', err);
      toast.error(`교육생 ${isEnrolled ? '제외' : '등록'} 중 오류가 발생했습니다.`);
    } finally {
      setStudentsLoading(false);
    }
  };

  // 출석 생성 핸들러
  const onAddAttendance = async (data) => {
    try {
      // 출석 데이터 준비
      const attendanceData = {
        classId: currentClass.id,
        studentId: data.studentId,
        status: data.status,
        registeredBy: user?.id || user?._id,
        date: selectedDate,
        note: data.note || ''
      };
      
      // API 호출
      const response = await authAxios.post('/attendance', attendanceData);
      
      // 출석 목록 업데이트
      const newAttendance = {
        ...response.data,
        id: response.data._id || response.data.id,
        studentName: currentStudents.find(s => s.id === data.studentId)?.name || '알 수 없음',
        className: currentClass.name
      };
      
      setAttendanceList([...attendanceList, newAttendance]);
      setIsAddingAttendance(false);
      reset();
    } catch (err) {
      console.error('Add attendance error:', err);
      alert('출석 기록 추가 중 오류가 발생했습니다.');
    }
  };

  // 출석 수정 핸들러
  const onEditAttendance = async (data) => {
    try {
      // API 호출
      const response = await authAxios.put(`/attendance/${currentAttendance.id}`, { 
        status: data.status,
        note: data.note 
      });
      
      // 출석 목록 업데이트
      const updatedAttendanceList = attendanceList.map(attendance => 
        attendance.id === currentAttendance.id 
          ? { 
              ...attendance, 
              status: data.status,
              note: data.note,
              time: data.status !== 'absent' ? (attendance.time || new Date().toLocaleTimeString()) : '',
            } 
          : attendance
      );
      
      setAttendanceList(updatedAttendanceList);
      setIsEditingAttendance(false);
      setCurrentAttendance(null);
      reset();
    } catch (err) {
      console.error('Edit attendance error:', err);
      alert('출석 기록 수정 중 오류가 발생했습니다.');
    }
  };

  // 출석 삭제 핸들러
  const onDeleteAttendance = async (id) => {
    if (!window.confirm('정말로 이 출석 기록을 삭제하시겠습니까?')) return;
    
    try {
      // API 호출
      await authAxios.delete(`/attendance/${id}`);
      
      // 출석 목록 업데이트
      const filteredAttendanceList = attendanceList.filter(attendance => attendance.id !== id);
      setAttendanceList(filteredAttendanceList);
    } catch (err) {
      console.error('Delete attendance error:', err);
      alert('출석 기록 삭제 중 오류가 발생했습니다.');
    }
  };

  // 출석 모드 시작 (강의 출결 관리)
  const startAttendanceMode = (classItem) => {
    setCurrentClass(classItem);
    setIsAttendanceMode(true);
    setSelectedDate(new Date().toISOString().split('T')[0]);
    fetchClassStudents(classItem.id);
    fetchAttendanceList(classItem.id, selectedDate);
  };

  // 수정 모드 시작 (출석 기록)
  const startEditAttendance = (attendance) => {
    setCurrentAttendance(attendance);
    setValue('status', attendance.status);
    setValue('note', attendance.note || '');
    setIsEditingAttendance(true);
  };

  // 컴포넌트 마운트 시 데이터 로딩
  useEffect(() => {
    fetchClasses();
    fetchInstructors();
    fetchAllStudents();
  }, [fetchClasses, fetchInstructors, fetchAllStudents]);

  // 선택된 날짜 변경 시 출석 목록 다시 로딩
  useEffect(() => {
    if (isAttendanceMode && currentClass) {
      fetchAttendanceList(currentClass.id, selectedDate);
    }
  }, [selectedDate, isAttendanceMode, currentClass]);

  // 강의 생성 핸들러
  const onAddClass = async (data) => {
    try {
      setIsSubmitting(true);
      
      // 현재 사용자 정보가 강사인 경우 
      const currentUserId = user?._id || user?.id;
      const currentUserName = user?.name || user?.username || '강사';
      
      // 날짜 포맷팅 (date, startTime, endTime)
      const classDate = new Date(data.date);
      
      // 시작 시간과 종료 시간을 Date 객체로 변환
      const [startHour, startMinute] = data.startTime.split(':').map(Number);
      const [endHour, endMinute] = data.endTime.split(':').map(Number);

      const startTimeDate = new Date(classDate);
      startTimeDate.setHours(startHour, startMinute, 0);

      const endTimeDate = new Date(classDate);
      endTimeDate.setHours(endHour, endMinute, 0);

      // 강의 위치 정보 처리
      let locationData = null;
      if (data.useCurrentLocation) {
        try {
          const position = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject);
          });
          
          locationData = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            address: data.address || '현재 위치'
          };
        } catch (error) {
          console.error('위치 정보를 가져올 수 없습니다:', error);
          toast.error('현재 위치를 가져올 수 없습니다. 주소를 직접 입력해주세요.');
          setIsSubmitting(false);
          return;
        }
      } else if (data.latitude && data.longitude) {
        locationData = {
          latitude: parseFloat(data.latitude),
          longitude: parseFloat(data.longitude),
          address: data.address || '입력된 위치'
        };
      }

      // API에 전송할 데이터 구성
      const classData = {
        name: data.name,
        instructorId: data.instructorId || currentUserId,
        instructorName: data.instructorName || currentUserName,
        date: classDate.toISOString(),
        startTime: startTimeDate.toISOString(),
        endTime: endTimeDate.toISOString(),
        description: data.description || '',
        room: data.room || '',
        category: data.category || '단기',
        structure: data.structure || '공통',
        module: data.module || '',
        location: locationData
      };

      console.log('서버로 전송할 데이터:', classData);
      
      // API 호출
      const response = await authAxios.post('/classes', classData);
      console.log('서버 응답:', response.data);
      
      // 강의 목록 업데이트
      fetchClasses();
      setIsAddingClass(false);
      reset();
      toast.success('강의가 성공적으로 추가되었습니다.');
      setIsSubmitting(false);
    } catch (err) {
      console.error('Add class error:', err);
      if (err.response) {
        console.error('서버 응답 상태:', err.response.status);
        console.error('서버 응답 데이터:', err.response.data);
        
        const errorMessage = err.response.data?.message || '강의 추가 중 오류가 발생했습니다.';
        toast.error(errorMessage);
      } else {
      toast.error('강의 추가 중 오류가 발생했습니다.');
      }
      setIsSubmitting(false);
    }
  };

  // 강의 수정 핸들러
  const onEditClass = async (data) => {
    try {
      setIsSubmitting(true);
      
      if (!currentClass || !currentClass.id) {
        console.error('현재 강의 ID가 없습니다:', currentClass);
        toast.error('강의 정보가 올바르지 않습니다. 다시 시도해주세요.');
        setIsSubmitting(false);
        return;
      }

      // 날짜 포맷팅 (date, startTime, endTime)
      const classDate = new Date(data.date);
      
      // 시작 시간과 종료 시간을 Date 객체로 변환
      const [startHour, startMinute] = data.startTime.split(':').map(Number);
      const [endHour, endMinute] = data.endTime.split(':').map(Number);

      const startTimeDate = new Date(classDate);
      startTimeDate.setHours(startHour, startMinute, 0);

      const endTimeDate = new Date(classDate);
      endTimeDate.setHours(endHour, endMinute, 0);

      // 강의 위치 정보 처리
      let locationData = currentClass.location || null;
      if (data.useCurrentLocation) {
        try {
          const position = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject);
          });
          
          locationData = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            address: data.address || '현재 위치'
          };
        } catch (error) {
          console.error('위치 정보를 가져올 수 없습니다:', error);
          toast.error('현재 위치를 가져올 수 없습니다. 주소를 직접 입력해주세요.');
          setIsSubmitting(false);
          return;
        }
      } else if (data.latitude && data.longitude) {
        locationData = {
          latitude: parseFloat(data.latitude),
          longitude: parseFloat(data.longitude),
          address: data.address || '입력된 위치'
        };
      }

      // API에 전송할 데이터 구성
      const classData = {
        name: data.name,
        instructorId: data.instructorId,
        instructorName: data.instructorName || instructors.find(i => i.id === data.instructorId)?.name || '',
        date: classDate.toISOString(),
        startTime: startTimeDate.toISOString(),
        endTime: endTimeDate.toISOString(),
        description: data.description || '',
        room: data.room || '',
        category: data.category || '단기',
        structure: data.structure || '공통',
        module: data.module || '',
        location: locationData
      };

      console.log('서버로 전송할 데이터:', classData);
      
      // API 호출
      console.log(`강의 ID ${currentClass.id} 수정 요청`);
      const response = await authAxios.put(`/classes/${currentClass.id}`, classData);
      console.log('서버 응답:', response.data);
      
      // 강의 목록 업데이트
      fetchClasses();
      setIsEditingClass(false);
      setCurrentClass(null);
      reset();
      toast.success('강의가 성공적으로 수정되었습니다.');
      setIsSubmitting(false);
    } catch (err) {
      console.error('Edit class error:', err);
      if (err.response) {
        console.error('서버 응답 상태:', err.response.status);
        console.error('서버 응답 데이터:', err.response.data);
        
        const errorMessage = err.response.data?.message || '강의 수정 중 오류가 발생했습니다.';
        toast.error(errorMessage);
      } else {
      toast.error('강의 수정 중 오류가 발생했습니다.');
      }
      setIsSubmitting(false);
    }
  };

  // 인라인 필드 수정 핸들러
  const handleInlineUpdate = async (classId, field, value) => {
    try {
      console.log(`인라인 수정 시도: ${field} 필드를 ${value}로 변경합니다.`);
      
      // 유효성 검사
      if (field === 'name' && (!value || value.trim() === '')) {
        alert('강의명은 비워둘 수 없습니다.');
        return;
      }
      
      if (field === 'instructorId' && (!value || value.trim() === '')) {
        alert('강사를 선택해주세요.');
        return;
      }
      
      // 수정 데이터 준비
      const updateData = {
        startTime: new Date(),  // 기본 날짜 필드를 포함해야 함
        endTime: new Date(Date.now() + 60 * 60 * 1000),
        [field]: value
      };
      
      // PUT 메서드로 변경 (백엔드가 PATCH를 지원하지 않을 수 있음)
      const response = await authAxios.put(`/classes/${classId}`, updateData);
      
      // 성공한 경우 로컬 상태 업데이트
      const updatedClasses = classes.map(classItem => {
        if (classItem.id === classId) {
          // 기존 클래스 데이터 복사
          const updatedClass = { ...classItem, [field]: value };
          
          // 강사 ID가 변경된 경우 강사 이름도 업데이트
          if (field === 'instructorId') {
            const instructor = instructors.find(ins => ins.id === value);
            if (instructor) {
              updatedClass.instructorName = instructor.name;
            }
          }
          
          return updatedClass;
        }
        return classItem;
      });
      
      setClasses(updatedClasses);
      setEditingRow(null);
      console.log(`${field} 수정 완료:`, response.data);
    } catch (err) {
      console.error('인라인 수정 오류:', err);
      
      // 오류 객체의 response 속성 확인
      if (err.response) {
        console.error('응답 상태:', err.response.status);
        console.error('응답 데이터:', err.response.data);
        
        // 백엔드에서 오류 메시지 제공하는 경우 표시
        if (err.response.data && err.response.data.message) {
          alert(`정보 수정 중 오류가 발생했습니다: ${err.response.data.message}`);
        } else {
          alert(`정보 수정 중 오류가 발생했습니다: ${err.response.status} ${err.response.statusText}`);
        }
      } else if (err.request) {
        // 요청은 전송되었지만 응답을 받지 못한 경우
        console.error('응답을 받지 못함:', err.request);
        alert('서버 응답이 없습니다. 네트워크 연결을 확인하세요.');
      } else {
        // 요청 설정 중에 오류가 발생한 경우
        alert(`정보 수정 중 오류가 발생했습니다: ${err.message}`);
      }

      // 원래의 값으로 폼 데이터 재설정
      const originalClass = classes.find(c => c.id === classId);
      if (originalClass) {
        setEditFormData(prev => ({
          ...prev,
          [field]: originalClass[field] || ''
        }));
      }
    }
  };

  // 인라인 편집 시작
  const startInlineEdit = (classItem) => {
    setEditingRow(classItem.id);
    setEditFormData({
      name: classItem.name || '',
      instructorId: classItem.instructorId || ''
    });
  };
  
  // 인라인 편집 취소
  const cancelInlineEdit = () => {
    setEditingRow(null);
    setEditFormData({
      name: '',
      instructorId: ''
    });
  };
  
  // 인라인 편집 변경 핸들러
  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData({
      ...editFormData,
      [name]: value
    });
  };

  // 강의 삭제 핸들러
  const onDeleteClass = async (id) => {
    if (!window.confirm('정말로 이 강의을 삭제하시겠습니까? 관련된 모든 출석 기록도 삭제됩니다.')) return;
    
    try {
      // API 호출
      await authAxios.delete(`/classes/${id}`);
      
      // 강의 목록 업데이트
      const filteredClasses = classes.filter(classItem => classItem.id !== id);
      setClasses(filteredClasses);
    } catch (err) {
      console.error('Delete class error:', err);
      alert('강의 삭제 중 오류가 발생했습니다.');
    }
  };

  // 수정 모드 시작
  const startEditClass = (classItem) => {
    setCurrentClass(classItem);
    setValue('name', classItem.name);
    setValue('instructorId', classItem.instructorId);
    setIsEditingClass(true);
  };

  // 교육생 목록 보기
  const viewStudents = async (classItem) => {
    try {
      // 로딩 시작
      setStudentsLoading(true);
      
      // 현재 강의 설정
    setCurrentClass(classItem);
    setIsViewingStudents(true);
      
      // 강의 최신 정보 조회 시도
      try {
        const classResponse = await authAxios.get(`/classes/${classItem.id}`);
        const updatedClass = {
          ...classResponse.data,
          id: classResponse.data._id || classResponse.data.id,
          students: classResponse.data.studentIds ? classResponse.data.studentIds.length : 0
        };
        
        // 강의 목록 업데이트
        setClasses(prevClasses => 
          prevClasses.map(c => c.id === classItem.id ? updatedClass : c)
        );
        
        // 현재 강의 업데이트
        setCurrentClass(updatedClass);
        
        console.log('강의 정보 업데이트 성공:', updatedClass);
      } catch (error) {
        console.error('강의 정보 업데이트 실패:', error);
        // 실패해도 계속 진행
      }
      
      // 교육생 목록 불러오기
      await fetchClassStudents(classItem.id);
      await fetchAllStudents(); // 전체 교육생 목록 새로 로드
      
      // 로딩 완료
      setStudentsLoading(false);
    } catch (error) {
      console.error('교육생 목록 보기 오류:', error);
      setStudentsLoading(false);
      toast.error('교육생 목록을 불러오는 중 오류가 발생했습니다.');
    }
  };
  
  // 교육생 추가 목록 필터링
  const filterAvailableStudents = useCallback(() => {
    // 현재 수강 중인 학생 ID 목록
    const enrolledStudentIds = currentStudents.map(student => student.id);
    
    // 아직 등록되지 않은 학생만 필터링
    let filteredStudents = allStudents.filter(student => !enrolledStudentIds.includes(student.id));
    
    // 기수별 필터링
    if (activeStudentTab !== 'all') {
      filteredStudents = filteredStudents.filter(student => student.batch === activeStudentTab);
    }
    
    return filteredStudents;
  }, [currentStudents, allStudents, activeStudentTab]);

  // QR 코드 생성
  const generateQRCode = async (classId) => {
    try {
      setQrLoading(true);
      
      // 현재 위치 정보 가져오기 (웹 브라우저 Geolocation API 사용)
      const getCurrentPosition = () => {
        return new Promise((resolve, reject) => {
          if (!navigator.geolocation) {
            reject(new Error('이 브라우저에서는 위치 정보를 지원하지 않습니다.'));
        return;
      }
      
          navigator.geolocation.getCurrentPosition(
            (position) => {
              resolve({
                latitude: position.coords.latitude,
                longitude: position.coords.longitude
              });
            },
            (error) => {
              console.error('위치 정보 가져오기 실패:', error);
              resolve({ latitude: 0, longitude: 0 }); // 기본값
            },
            { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
          );
        });
      };
      
      // 위치 정보 가져오기
      const position = await getCurrentPosition();
      
      // 선택된 클래스 정보 가져오기
      const classInfo = classes.find(c => c.id === classId);
      
      // 현재 사용자 정보
      const instructorId = user?.id || user?._id;
      const instructorName = user?.name || '';
      
      // QR 코드 데이터 생성
      const qrData = {
        classId: classId,
        instructorId: instructorId,
        instructorName: instructorName,
        type: 'checkin', // 기본값은 입실
        generatedAt: new Date(),
        expiresAt: new Date(Date.now() + 60 * 60 * 1000), // 1시간 후 만료
        location: {
          latitude: position.latitude,
          longitude: position.longitude,
          address: classInfo?.location?.address || ''
        }
      };
      
      console.log('QR 코드 생성 데이터:', qrData);
      
      // 백엔드 API 호출
      const response = await authAxios.post('/qr-codes', qrData);
      
      // 서버로부터 받은 QR 코드 데이터 (실제로는 응답 구조에 맞게 수정 필요)
      const qrCodeData = response.data.code || response.data;
      
      // 현재 선택된 클래스와 QR 코드 저장
      const selectedClass = classes.find(c => c.id === classId);
      setCurrentClass(selectedClass);
      setCurrentQRCode(qrCodeData);
      
      // QR 코드 모달 표시
      setShowQRModal(true);
      setQrLoading(false);
    } catch (err) {
      console.error('QR 코드 생성 오류:', err);
      toast.error('QR 코드 생성 중 오류가 발생했습니다.');
      setQrLoading(false);
    }
  };

  // QR 코드 생성 모달
  const renderQRCodeModal = () => {
    return (
      <Modal
        title="출석 QR 코드"
        open={showQRModal}
        onCancel={() => setShowQRModal(false)}
        footer={[
          <Button key="print" type="default" onClick={printQRCode} icon={<PrinterOutlined />}>
            인쇄하기
          </Button>,
          <Button key="download" type="primary" onClick={downloadQRCode} icon={<DownloadOutlined />}>
            QR 코드 다운로드
          </Button>,
          <Button key="close" onClick={() => setShowQRModal(false)}>
            닫기
          </Button>
        ]}
        width={400}
        centered
      >
        <div id="qrcode-canvas" style={{ textAlign: 'center' }}>
          {qrLoading ? (
            <div style={{ padding: '30px' }}>
              <Spin tip="QR 코드 생성 중..." />
            </div>
          ) : currentQRCode ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <p>QR 코드 유효시간: 15분</p>
              <p>현재 강의: {currentClass?.name}</p>
              <div style={{ margin: '10px auto' }}>
                <QRCodeSVG id="qrcode-svg" value={currentQRCode} size={250} />
              </div>
              <div style={{ marginTop: 15, wordBreak: 'break-all' }}>
                <Typography.Text strong>URL: </Typography.Text>
                <Typography.Link href={currentQRCode} target="_blank" copyable>
                  {currentQRCode}
                </Typography.Link>
              </div>
            </div>
          ) : (
            <Empty description="QR 코드가 생성되지 않았습니다." />
          )}
        </div>
      </Modal>
    );
  };

  // 강의 편집 모달 렌더링
  const renderEditClassModal = () => {
    if (!isEditingClass || !currentClass) return null;
    
    // 현재 날짜와 시간 가져오기
    const classDate = currentClass.date ? new Date(currentClass.date) : new Date();
    const dateString = classDate.toISOString().split('T')[0];
    
    // 시작 시간과 종료 시간 처리
    const startTime = currentClass.startTime 
      ? new Date(currentClass.startTime).toTimeString().substr(0, 5) 
      : '09:00';
    
    const endTime = currentClass.endTime 
      ? new Date(currentClass.endTime).toTimeString().substr(0, 5) 
      : '18:00';
    
    // 위치 정보 처리
    const location = currentClass.location || {};

  return (
      <Modal
        title="강의 정보 수정"
        open={isEditingClass}
        onCancel={() => {
          setIsEditingClass(false);
          setCurrentClass(null);
          reset();
        }}
        footer={null}
        width={700}
      >
        <form onSubmit={handleSubmit(onEditClass)} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* 기본 정보 */}
            <div className="md:col-span-2">
              <h3 className="font-bold mb-2 pb-1 border-b">기본 정보</h3>
            </div>
            
            {/* 강의명 */}
      <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                강의명 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                defaultValue={currentClass.name}
                {...register('name', { required: '강의명은 필수입니다.' })}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>
            
            {/* 강사 선택 */}
            <div>
              <label htmlFor="instructorId" className="block text-sm font-medium text-gray-700">
                담당 강사 <span className="text-red-500">*</span>
              </label>
              <select
                id="instructorId"
                defaultValue={currentClass.instructorId}
                {...register('instructorId', { required: '담당 강사는 필수입니다.' })}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              >
                <option value="">-- 강사 선택 --</option>
                {instructors.map(instructor => (
                  <option key={instructor.id} value={instructor.id}>
                    {instructor.name}
                  </option>
                ))}
              </select>
              {errors.instructorId && (
                <p className="mt-1 text-sm text-red-600">{errors.instructorId.message}</p>
              )}
            </div>
            
            {/* 강의 날짜 */}
            <div>
              <label htmlFor="date" className="block text-sm font-medium text-gray-700">
                강의 날짜 <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                id="date"
                defaultValue={dateString}
                {...register('date', { required: '강의 날짜는 필수입니다.' })}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              />
              {errors.date && (
                <p className="mt-1 text-sm text-red-600">{errors.date.message}</p>
              )}
            </div>
            
            {/* 강의실 */}
            <div>
              <label htmlFor="room" className="block text-sm font-medium text-gray-700">
                강의실
              </label>
              <input
                type="text"
                id="room"
                defaultValue={currentClass.room}
                {...register('room')}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              />
            </div>
            
            {/* 시작 시간 */}
            <div>
              <label htmlFor="startTime" className="block text-sm font-medium text-gray-700">
                시작 시간 <span className="text-red-500">*</span>
              </label>
              <input
                type="time"
                id="startTime"
                defaultValue={startTime}
                {...register('startTime', { required: '시작 시간은 필수입니다.' })}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              />
              {errors.startTime && (
                <p className="mt-1 text-sm text-red-600">{errors.startTime.message}</p>
              )}
            </div>
            
            {/* 종료 시간 */}
            <div>
              <label htmlFor="endTime" className="block text-sm font-medium text-gray-700">
                종료 시간 <span className="text-red-500">*</span>
              </label>
              <input
                type="time"
                id="endTime"
                defaultValue={endTime}
                {...register('endTime', { required: '종료 시간은 필수입니다.' })}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              />
              {errors.endTime && (
                <p className="mt-1 text-sm text-red-600">{errors.endTime.message}</p>
              )}
            </div>
            
            {/* 강의 구분 */}
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                강의 구분
              </label>
              <div className="mt-1 flex space-x-4">
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="category-short"
                    value="단기"
                    defaultChecked={currentClass.category === '단기'}
                {...register('category')}
                    className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                  />
                  <label htmlFor="category-short" className="ml-2 block text-sm text-gray-700">
                    단기
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="category-medium"
                    value="중기"
                    defaultChecked={currentClass.category === '중기'}
                    {...register('category')}
                    className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                  />
                  <label htmlFor="category-medium" className="ml-2 block text-sm text-gray-700">
                    중기
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="category-long"
                    value="장기"
                    defaultChecked={currentClass.category === '장기'}
                    {...register('category')}
                    className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                  />
                  <label htmlFor="category-long" className="ml-2 block text-sm text-gray-700">
                    장기
                  </label>
                </div>
              </div>
            </div>
            
            {/* 강의 구성 */}
            <div>
              <label htmlFor="structure" className="block text-sm font-medium text-gray-700">
                강의 구성
              </label>
              <select
                id="structure"
                defaultValue={currentClass.structure}
                {...register('structure')}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              >
                <option value="공통">공통</option>
                <option value="지역">지역</option>
                <option value="특화">특화</option>
              </select>
            </div>
            
            {/* 모듈 */}
            <div>
              <label htmlFor="module" className="block text-sm font-medium text-gray-700">
                모듈
              </label>
              <input
                type="text"
                id="module"
                defaultValue={currentClass.module}
                {...register('module')}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              />
            </div>
            
            {/* 설명 */}
            <div className="md:col-span-2">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                강의 설명
              </label>
              <textarea
                id="description"
                rows={3}
                defaultValue={currentClass.description}
                {...register('description')}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              />
            </div>
            
            {/* 위치 정보 */}
            <div className="md:col-span-2">
              <h3 className="font-bold mb-2 pb-1 border-b">강의실 위치 정보</h3>
            </div>
            
            {/* 현재 위치 사용 */}
            <div className="md:col-span-2">
              <div className="flex items-center">
              <input
                  type="checkbox"
                  id="useCurrentLocation"
                  {...register('useCurrentLocation')}
                  className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                />
                <label htmlFor="useCurrentLocation" className="ml-2 block text-sm text-gray-700">
                  현재 위치 사용
                </label>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                체크하면 현재 위치 정보를 가져옵니다. 권한을 요청할 수 있습니다.
        </p>
      </div>

            {/* 위도 */}
            <div>
              <label htmlFor="latitude" className="block text-sm font-medium text-gray-700">
                위도
              </label>
              <input
                type="text"
                id="latitude"
                defaultValue={location.latitude}
                {...register('latitude')}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              />
            </div>
            
            {/* 경도 */}
            <div>
              <label htmlFor="longitude" className="block text-sm font-medium text-gray-700">
                경도
              </label>
              <input
                type="text"
                id="longitude"
                defaultValue={location.longitude}
                {...register('longitude')}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              />
            </div>
            
            {/* 주소 */}
            <div className="md:col-span-2">
              <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                주소
              </label>
              <input
                type="text"
                id="address"
                defaultValue={location.address}
                {...register('address')}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              />
            </div>
          </div>
          
          <div className="pt-4 flex justify-end space-x-3">
        <button
          type="button"
          onClick={() => {
                setIsEditingClass(false);
                setCurrentClass(null);
            reset();
              }}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 flex items-center space-x-1"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>저장 중...</span>
                </>
              ) : (
                <span>저장</span>
              )}
        </button>
      </div>
        </form>
      </Modal>
    );
  };

  // 강의 추가 모달 렌더링
  const renderAddClassModal = () => {
    if (!isAddingClass) return null;
    
    // 현재 날짜 가져오기
    const today = new Date();
    const dateString = today.toISOString().split('T')[0];
    
    return (
      <Modal
        title="새 강의 추가"
        open={isAddingClass}
        onCancel={() => {
          setIsAddingClass(false);
          reset();
        }}
        footer={null}
        width={700}
      >
        <form onSubmit={handleSubmit(onAddClass)} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* 기본 정보 */}
            <div className="md:col-span-2">
              <h3 className="font-bold mb-2 pb-1 border-b">기본 정보</h3>
            </div>
            
            {/* 강의명 */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                강의명 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                {...register('name', { required: '강의명은 필수입니다.' })}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                />
                {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>

            {/* 강사 선택 */}
              <div>
                <label htmlFor="instructorId" className="block text-sm font-medium text-gray-700">
                담당 강사 <span className="text-red-500">*</span>
                </label>
                <select
                  id="instructorId"
                {...register('instructorId', { required: '담당 강사는 필수입니다.' })}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                >
                <option value="">-- 강사 선택 --</option>
                {instructors.map(instructor => (
                    <option key={instructor.id} value={instructor.id}>
                      {instructor.name}
                    </option>
                  ))}
                </select>
                {errors.instructorId && (
                <p className="mt-1 text-sm text-red-600">{errors.instructorId.message}</p>
                )}
              </div>

            {/* 강의 날짜 */}
              <div>
              <label htmlFor="date" className="block text-sm font-medium text-gray-700">
                강의 날짜 <span className="text-red-500">*</span>
                </label>
                <input
                type="date"
                id="date"
                defaultValue={dateString}
                {...register('date', { required: '강의 날짜는 필수입니다.' })}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              />
              {errors.date && (
                <p className="mt-1 text-sm text-red-600">{errors.date.message}</p>
                )}
              </div>

            {/* 강의실 */}
              <div>
                <label htmlFor="room" className="block text-sm font-medium text-gray-700">
                  강의실
                </label>
                <input
                  type="text"
                  id="room"
                {...register('room')}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              />
            </div>
            
            {/* 시작 시간 */}
            <div>
              <label htmlFor="startTime" className="block text-sm font-medium text-gray-700">
                시작 시간 <span className="text-red-500">*</span>
              </label>
              <input
                type="time"
                id="startTime"
                defaultValue="09:00"
                {...register('startTime', { required: '시작 시간은 필수입니다.' })}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              />
              {errors.startTime && (
                <p className="mt-1 text-sm text-red-600">{errors.startTime.message}</p>
                )}
              </div>

            {/* 종료 시간 */}
            <div>
              <label htmlFor="endTime" className="block text-sm font-medium text-gray-700">
                종료 시간 <span className="text-red-500">*</span>
              </label>
              <input
                type="time"
                id="endTime"
                defaultValue="18:00"
                {...register('endTime', { required: '종료 시간은 필수입니다.' })}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              />
              {errors.endTime && (
                <p className="mt-1 text-sm text-red-600">{errors.endTime.message}</p>
              )}
            </div>
            
            {/* 강의 구분 */}
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                강의 구분
              </label>
              <div className="mt-1 flex space-x-4">
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="category-short"
                    value="단기"
                    defaultChecked
                {...register('category')}
                    className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                  />
                  <label htmlFor="category-short" className="ml-2 block text-sm text-gray-700">
                    단기
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="category-medium"
                    value="중기"
                    {...register('category')}
                    className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                  />
                  <label htmlFor="category-medium" className="ml-2 block text-sm text-gray-700">
                    중기
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="category-long"
                    value="장기"
                    {...register('category')}
                    className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                  />
                  <label htmlFor="category-long" className="ml-2 block text-sm text-gray-700">
                    장기
                  </label>
                </div>
              </div>
            </div>
            
            {/* 강의 구성 */}
            <div>
              <label htmlFor="structure" className="block text-sm font-medium text-gray-700">
                강의 구성
              </label>
              <select
                id="structure"
                defaultValue="공통"
                {...register('structure')}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              >
                <option value="공통">공통</option>
                <option value="지역">지역</option>
                <option value="특화">특화</option>
              </select>
            </div>
            
            {/* 모듈 */}
            <div>
              <label htmlFor="module" className="block text-sm font-medium text-gray-700">
                모듈
              </label>
              <input
                type="text"
                id="module"
                {...register('module')}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              />
            </div>
            
            {/* 설명 */}
            <div className="md:col-span-2">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                강의 설명
              </label>
              <textarea
                id="description"
                rows={3}
                {...register('description')}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              />
            </div>
            
            {/* 위치 정보 */}
            <div className="md:col-span-2">
              <h3 className="font-bold mb-2 pb-1 border-b">강의실 위치 정보</h3>
            </div>
            
            {/* 현재 위치 사용 */}
            <div className="md:col-span-2">
              <div className="flex items-center">
              <input
                  type="checkbox"
                  id="useCurrentLocation"
                  {...register('useCurrentLocation')}
                  className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                />
                <label htmlFor="useCurrentLocation" className="ml-2 block text-sm text-gray-700">
                  현재 위치 사용
                </label>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                체크하면 현재 위치 정보를 가져옵니다. 권한을 요청할 수 있습니다.
              </p>
            </div>
            
            {/* 위도 */}
            <div>
              <label htmlFor="latitude" className="block text-sm font-medium text-gray-700">
                위도
              </label>
              <input
                type="text"
                id="latitude"
                {...register('latitude')}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              />
            </div>
            
            {/* 경도 */}
            <div>
              <label htmlFor="longitude" className="block text-sm font-medium text-gray-700">
                경도
              </label>
              <input
                type="text"
                id="longitude"
                {...register('longitude')}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              />
            </div>
            
            {/* 주소 */}
            <div className="md:col-span-2">
              <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                주소
              </label>
              <input
                type="text"
                id="address"
                {...register('address')}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              />
            </div>
          </div>
          
          <div className="pt-4 flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => {
                setIsAddingClass(false);
                reset();
              }}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              취소
            </button>
                <button
                  type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 flex items-center space-x-1"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>저장 중...</span>
                </>
              ) : (
                <span>저장</span>
              )}
                </button>
          </div>
        </form>
      </Modal>
    );
  };

  // 정렬 함수 추가
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // 정렬된 클래스 목록 가져오기
  const getSortedClasses = useCallback(() => {
    // 필터링된 클래스 목록 가져오기
    let filteredClasses = [...classes];
    
    // 필터 적용
    if (filters.instructorId) {
      filteredClasses = filteredClasses.filter(c => 
        c.instructorId === filters.instructorId
      );
    }
    
    if (filters.category) {
      filteredClasses = filteredClasses.filter(c => 
        c.category === filters.category
      );
    }
    
    if (filters.structure) {
      filteredClasses = filteredClasses.filter(c => 
        c.structure === filters.structure
      );
    }
    
    // 정렬 적용
    if (sortConfig.key) {
      filteredClasses.sort((a, b) => {
        // null 체크
        const aValue = a[sortConfig.key] || '';
        const bValue = b[sortConfig.key] || '';
        
        // 문자열 비교
        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    
    return filteredClasses;
  }, [classes, sortConfig, filters]);

  // 필터 변경 핸들러
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };
  
  // 필터 초기화 핸들러
  const clearFilters = () => {
    setFilters({
      instructorId: '',
      category: '',
      structure: ''
    });
  };

  // 현재 등록된 학생들 기수별 필터링
  const getCurrentStudentsByBatch = useCallback(() => {
    if (activeStudentTab === 'all') {
      return currentStudents;
    }
    
    return currentStudents.filter(student => student.batch === activeStudentTab);
  }, [currentStudents, activeStudentTab]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">교육생 강의 등록</h1>
        <p className="mt-1 text-sm text-gray-500">
          강의을 생성하고 관리할 수 있습니다.
        </p>
      </div>

      <div className="flex justify-between items-center">
                <button
                  type="button"
          onClick={() => setShowFilters(!showFilters)}
          className="bg-white hover:bg-gray-100 text-gray-800 font-semibold py-2 px-4 border border-gray-300 rounded shadow-sm text-sm"
                >
          {showFilters ? '필터 숨기기' : '필터 표시'}
                </button>
              </div>

      {/* 필터링 패널 */}
      {showFilters && (
        <div className="bg-white p-4 rounded-md shadow mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label htmlFor="instructorFilter" className="block text-sm font-medium text-gray-700 mb-1">
                강사 필터
              </label>
              <select
                id="instructorFilter"
                name="instructorId"
                value={filters.instructorId}
                onChange={handleFilterChange}
                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              >
                <option value="">모든 강사</option>
                {instructors.map(instructor => (
                  <option key={instructor.id} value={instructor.id}>
                    {instructor.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label htmlFor="categoryFilter" className="block text-sm font-medium text-gray-700 mb-1">
                분류 필터
              </label>
              <select
                id="categoryFilter"
                name="category"
                value={filters.category}
                onChange={handleFilterChange}
                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              >
                <option value="">모든 분류</option>
                <option value="단기">단기</option>
                <option value="중기">중기</option>
                <option value="장기">장기</option>
              </select>
            </div>
            
            <div>
              <label htmlFor="structureFilter" className="block text-sm font-medium text-gray-700 mb-1">
                구성 필터
              </label>
              <select
                id="structureFilter"
                name="structure"
                value={filters.structure}
                onChange={handleFilterChange}
                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              >
                <option value="">모든 구성</option>
                <option value="공통">공통</option>
                <option value="지역">지역</option>
                <option value="특화">특화</option>
              </select>
            </div>
            
            <div className="flex items-end">
              <button
                type="button"
                onClick={clearFilters}
                className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-2 rounded border border-gray-300 text-sm"
              >
                필터 초기화
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 강의 추가 폼 */}
      {isAddingClass && (
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              새 강의 추가
            </h3>
            <form onSubmit={handleSubmit(onAddClass)} className="mt-5 space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  강의 이름
                </label>
                <input
                  type="text"
                  id="name"
                  {...register('name', { required: '강의 이름은 필수입니다.' })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
                {errors.name && (
                  <p className="mt-2 text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="instructorId" className="block text-sm font-medium text-gray-700">
                  담당 강사
                </label>
                <select
                  id="instructorId"
                  {...register('instructorId', { required: '담당 강사를 선택해주세요.' })}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                >
                  <option value="">강사 선택</option>
                  {instructors
                    .map((instructor) => (
                    <option key={instructor.id} value={instructor.id}>
                      {instructor.name}
                    </option>
                  ))}
                </select>
                {errors.instructorId && (
                  <p className="mt-2 text-sm text-red-600">{errors.instructorId.message}</p>
                )}
              </div>

              <div className="flex space-x-2">
                <button
                  type="submit"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  저장
                </button>
                <button
                  type="button"
                  onClick={() => setIsAddingClass(false)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  취소
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 강의 수정 폼 */}
      {isEditingClass && currentClass && (
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              강의 정보 수정 - {currentClass.name}
            </h3>
            <form onSubmit={handleSubmit(onEditClass)} className="mt-5 space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  강의 이름
                </label>
                <input
                  type="text"
                  id="name"
                  {...register('name', { required: '강의 이름은 필수입니다.' })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
                {errors.name && (
                  <p className="mt-2 text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="instructorId" className="block text-sm font-medium text-gray-700">
                  담당 강사
                </label>
                <select
                  id="instructorId"
                  {...register('instructorId', { required: '담당 강사를 선택해주세요.' })}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                >
                  <option value="">강사 선택</option>
                  {instructors
                    .map((instructor) => (
                    <option key={instructor.id} value={instructor.id}>
                      {instructor.name}
                    </option>
                  ))}
                </select>
                {errors.instructorId && (
                  <p className="mt-2 text-sm text-red-600">{errors.instructorId.message}</p>
                )}
              </div>

              <div className="flex space-x-2">
                <button
                  type="submit"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  저장
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsEditingClass(false);
                    setCurrentClass(null);
                  }}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  취소
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 교육생 목록 */}
      {isViewingStudents && currentClass && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 py-6">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => {
              setIsViewingStudents(false);
              setCurrentClass(null);
              setCurrentStudents([]);
            }}></div>
            <div className="bg-white rounded-lg overflow-hidden shadow-xl transform transition-all sm:max-w-5xl sm:w-full max-h-[90vh] flex flex-col">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 flex-shrink-0">
                    <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                        수강생 관리 - {currentClass.name}
              </h3>
              <button
                type="button"
                onClick={() => {
                  setIsViewingStudents(false);
                  setCurrentClass(null);
                  setCurrentStudents([]);
                }}
                className="text-sm font-medium text-indigo-600 hover:text-indigo-500"
              >
                닫기
              </button>
            </div>
            
                {/* 기수별 탭 추가 */}
                <div className="border-b border-gray-200 mb-4">
                  <nav className="-mb-px flex space-x-4">
                    <button
                      onClick={() => setActiveStudentTab('all')}
                      className={`${
                        activeStudentTab === 'all'
                          ? 'border-indigo-500 text-indigo-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
                    >
                      전체
                    </button>
                    
                    {studentBatches.map((batch) => (
                      <button
                        key={batch}
                        onClick={() => setActiveStudentTab(batch)}
                        className={`${
                          activeStudentTab === batch
                            ? 'border-indigo-500 text-indigo-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
                      >
                        {batch}
                      </button>
                    ))}
                  </nav>
                </div>
              </div>
            
              <div className="flex-grow overflow-auto px-4 pb-4 sm:px-6" style={{ maxHeight: 'calc(90vh - 120px)' }}>
                    {/* 로딩 표시 */}
                    {(studentsLoading || loadingAllStudents) ? (
                  <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* 왼쪽: 전체 교육생 목록 */}
                    <div className="flex flex-col h-full">
                      <h4 className="text-md font-medium text-gray-700 mb-2">
                        {activeStudentTab === 'all' ? '전체 교육생 목록' : `${activeStudentTab} 교육생 목록`}
                      </h4>
                      <div className="border border-gray-200 rounded-md overflow-hidden flex-grow h-[400px] flex flex-col">
                        <div className="overflow-y-auto flex-grow">
                              {filterAvailableStudents().length === 0 ? (
                                <p className="p-4 text-center text-gray-500">추가 가능한 교육생이 없습니다.</p>
                              ) : (
                                <ul className="divide-y divide-gray-200">
                                  {filterAvailableStudents().map((student) => (
                                    <li 
                                      key={student.id} 
                                      className="pl-3 pr-4 py-3 flex items-center justify-between text-sm hover:bg-gray-50"
                                    >
                      <div className="w-0 flex-1 flex items-center">
                        <span className="flex-1 w-0 truncate">
                                      {student.name} ({student.phone || student.studentId}) {student.batch && `- ${student.batch}`}
                        </span>
                      </div>
                      <div className="ml-4 flex-shrink-0">
                        <button
                          type="button"
                                          onClick={() => toggleStudentEnrollment(currentClass.id, student.id, false)}
                                          className="font-medium text-indigo-600 hover:text-indigo-500"
                        >
                                          추가
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* 오른쪽: 강의에 등록된 교육생 목록 */}
                    <div className="flex flex-col h-full">
                      <h4 className="text-md font-medium text-gray-700 mb-2">
                        {activeStudentTab === 'all' ? '등록된 교육생 목록' : `등록된 ${activeStudentTab} 교육생 목록`}
                      </h4>
                      <div className="border border-gray-200 rounded-md overflow-hidden flex-grow h-[400px] flex flex-col">
                        <div className="overflow-y-auto flex-grow">
                          {getCurrentStudentsByBatch().length === 0 ? (
                                <p className="p-4 text-center text-gray-500">등록된 교육생이 없습니다.</p>
                              ) : (
                                <ul className="divide-y divide-gray-200">
                              {getCurrentStudentsByBatch().map((student) => (
                                    <li 
                                      key={student.id} 
                                      className="pl-3 pr-4 py-3 flex items-center justify-between text-sm hover:bg-gray-50"
                                    >
                                      <div className="w-0 flex-1 flex items-center">
                                        <span className="flex-1 w-0 truncate">
                                      {student.name} ({student.studentId}) {student.batch && `- ${student.batch}`}
                                        </span>
                                      </div>
                                      <div className="ml-4 flex-shrink-0">
                <button
                  type="button"
                                          onClick={() => toggleStudentEnrollment(currentClass.id, student.id, true)}
                                          className="font-medium text-red-600 hover:text-red-500"
                                        >
                                          삭제
                </button>
                                      </div>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 강의 목록 */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        ) : error ? (
          <div className="px-4 py-5 sm:px-6">
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          </div>
        ) : getSortedClasses().length === 0 ? (
          <div className="px-4 py-5 sm:p-6 text-center text-gray-500">
            {classes.length === 0 ? '등록된 강의이 없습니다. 새 강의을 추가해주세요.' : '필터 조건에 맞는 강의가 없습니다.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th 
                    scope="col" 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => requestSort('name')}
                  >
                    <div className="flex items-center">
                    강의명
                      {sortConfig.key === 'name' && (
                        <span className="ml-1">
                          {sortConfig.direction === 'ascending' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th 
                    scope="col" 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => requestSort('instructorName')}
                  >
                    <div className="flex items-center">
                    강사
                      {sortConfig.key === 'instructorName' && (
                        <span className="ml-1">
                          {sortConfig.direction === 'ascending' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th 
                    scope="col" 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => requestSort('category')}
                  >
                    <div className="flex items-center">
                      분류
                      {sortConfig.key === 'category' && (
                        <span className="ml-1">
                          {sortConfig.direction === 'ascending' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th 
                    scope="col" 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => requestSort('structure')}
                  >
                    <div className="flex items-center">
                      구성
                      {sortConfig.key === 'structure' && (
                        <span className="ml-1">
                          {sortConfig.direction === 'ascending' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th 
                    scope="col" 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => requestSort('students')}
                  >
                    <div className="flex items-center">
                    수강 인원
                      {sortConfig.key === 'students' && (
                        <span className="ml-1">
                          {sortConfig.direction === 'ascending' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {getSortedClasses().map((classItem) => (
                  <tr key={classItem.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                          {classItem.name}
                        </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {instructors.find(i => i.id === classItem.instructorId)?.name || '-'}
                        </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {classItem.category || '단기'}
                        </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {classItem.structure || '공통'}
                        </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div 
                        className="text-sm text-gray-900 cursor-pointer hover:bg-gray-100 p-1 rounded"
                          onClick={() => viewStudents(classItem)}
                      >
                        {classItem.students || 0}명
                      </div>
                    </td>
                    
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* QR 코드 모달 */}
      {renderQRCodeModal()}

      {/* 출결현황 모달 */}
      {showAttendanceStatus && currentClass && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-4/5 max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                {currentClass.name} - 출결현황
              </h2>
              <button
                onClick={() => setShowAttendanceStatus(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                닫기
              </button>
                      </div>
                    
            {attendanceStatusLoading ? (
              <div className="flex justify-center my-8">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                    </div>
            ) : (
              <>
                {/* 출결 요약 정보 */}
                <div className="mb-6 grid grid-cols-4 gap-4">
                  <div className="p-4 bg-gray-100 rounded-lg text-center">
                    <div className="text-lg font-semibold">총 인원</div>
                    <div className="text-2xl">{attendanceStats.totalStudents}명</div>
                  </div>
                  <div className="p-4 bg-green-100 rounded-lg text-center">
                    <div className="text-lg font-semibold">출석</div>
                    <div className="text-2xl">{attendanceStats.presentCount}명</div>
                </div>
                  <div className="p-4 bg-yellow-100 rounded-lg text-center">
                    <div className="text-lg font-semibold">지각</div>
                    <div className="text-2xl">{attendanceStats.lateCount}명</div>
              </div>
                  <div className="p-4 bg-red-100 rounded-lg text-center">
                    <div className="text-lg font-semibold">결석</div>
                    <div className="text-2xl">{attendanceStats.absentCount}명</div>
                  </div>
                </div>
                
                {/* 날짜 선택 및 엑셀 다운로드 */}
                <div className="flex justify-between items-center mb-4">
                  <div className="flex items-center space-x-2">
                    <label htmlFor="attendance-date">날짜 선택:</label>
                    <input
                      type="date"
                      id="attendance-date"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      className="border rounded px-2 py-1"
                    />
                  </div>
                <button
                    onClick={exportAttendanceToExcel}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                >
                    엑셀 다운로드
                </button>
              </div>
                
                {/* 학생 목록 테이블 */}
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white border">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="py-2 px-4 border">번호</th>
                        <th className="py-2 px-4 border">이름</th>
                        <th className="py-2 px-4 border">학번</th>
                        <th className="py-2 px-4 border">상태</th>
                        <th className="py-2 px-4 border">시간</th>
                        <th className="py-2 px-4 border">비고</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attendanceStatusList.length > 0 ? (
                        attendanceStatusList.map((student, index) => (
                          <tr key={student.id} className="hover:bg-gray-50">
                            <td className="py-2 px-4 border text-center">{index + 1}</td>
                            <td className="py-2 px-4 border">{student.name}</td>
                            <td className="py-2 px-4 border">{student.phone}</td>
                            <td className="py-2 px-4 border">
                <button
                                onClick={() => toggleAttendanceStatus(student)}
                                className={`px-2 py-1 rounded text-xs ${getStatusColor(student.status)}`}
                >
                                {getStatusDisplayName(student.status)}
                </button>
                            </td>
                            <td className="py-2 px-4 border">
                              {student.time || '-'}
                            </td>
                            <td className="py-2 px-4 border">
                              {student.note || '-'}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" className="py-4 text-center">
                            등록된 학생이 없거나 출결 데이터가 없습니다.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
            </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassManagement; 